import { useNavigate, useParams } from '@tanstack/react-router';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Menu } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { DeleteMenuDialog } from '../components/delete-menu-dialog';
import { MenuDialog } from '../components/menu-dialog';
import { MenuTreeEditor } from '../components/menu-tree-editor';
import { useMenu } from '../hooks/use-menu';

const ROUTE = '/_authed/_active/s/$storeId/content/menus/$menuId' as const;

function DetailsCard({ storeId, menu }: { storeId: string; menu: Menu }) {
  const { t } = useTranslation('menus');
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState<Menu | null>(null);

  return (
    <>
      <FormSection title={t('details.title')} description={t('details.description')}>
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
          <dt className="text-muted-foreground">{t('fields.title')}</dt>
          <dd>{menu.title}</dd>
          <dt className="text-muted-foreground">{t('fields.handle')}</dt>
          <dd className="font-mono text-[13px]">{menu.handle}</dd>
        </dl>
        <Can permission="content.write">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil aria-hidden />
              {t('actions.rename')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleting(menu)}
            >
              <Trash2 aria-hidden />
              {t('actions.delete')}
            </Button>
          </div>
        </Can>
      </FormSection>
      {editing ? <MenuDialog storeId={storeId} menu={menu} open onOpenChange={setEditing} /> : null}
      <DeleteMenuDialog
        storeId={storeId}
        menu={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/content/menus', params: { storeId } });
        }}
      />
    </>
  );
}

function MenuEditor() {
  const { t } = useTranslation('menus');
  const format = useFormat();
  const store = useStore();
  const { menuId } = useParams({ from: ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: menu } = useMenu(store.id, menuId);
  if (!menu) return null;

  return (
    <PageContent>
      <PageHeader
        title={menu.title}
        description={
          <span title={format.utc(menu.updatedAt)}>
            <code>{menu.handle}</code>
            {' · '}
            {t('updatedAt', { when: format.relative(menu.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/content/menus', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <FormLayout aside={<DetailsCard storeId={store.id} menu={menu} />}>
        <MenuTreeEditor key={menu.updatedAt} storeId={store.id} menu={menu} />
      </FormLayout>
    </PageContent>
  );
}

export function MenuEditorPage() {
  return (
    <FeatureErrorBoundary name="menus.edit">
      <MenuEditor />
    </FeatureErrorBoundary>
  );
}
